import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  X,
  Sparkles,
  FileText,
  ClipboardList,
  Calendar,
  User,
  ChevronRight,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
} from "../quotationMasterListShared";
import { formatINR } from "../../../../Utils/helper";

interface SelectDocumentModalProps {
  open: boolean;
  list: any[];
  type: "quotation" | "order";
  onClose: () => void;
  onSelect: (doc: any) => void;
}

export const SelectDocumentModal: React.FC<SelectDocumentModalProps> = ({
  open,
  list,
  type,
  onClose,
  onSelect,
}) => {
  if (!open || list.length === 0) return null;

  const isQuotation = type === "quotation";
  const DocIcon = isQuotation ? FileText : ClipboardList;

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                {isQuotation ? "Choose quotation" : "Choose order"}
              </HeroBadge>
              <ModalTitle>
                Select {isQuotation ? "quotation" : "order"} to send
              </ModalTitle>
              <ModalSubtitle>
                Multiple {isQuotation ? "quotations" : "orders"} found for this
                customer. Pick the one you want to share.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <CountPill>
            {list.length} {isQuotation ? "quotation" : "order"}
            {list.length === 1 ? "" : "s"} available
          </CountPill>

          <DocList>
            {list.map((doc: any) => {
              const docNo = isQuotation ? doc.quotation_no : doc.order_no;
              const date = isQuotation
                ? doc.quotation_date
                : doc.order_date || doc.created_at?.split("T")[0];
              const amount = Number(doc.grand_total ?? 0);

              return (
                <DocCard key={doc.id} type="button" onClick={() => onSelect(doc)}>
                  <DocIconWrap>
                    <DocIcon size={18} strokeWidth={1.8} />
                  </DocIconWrap>
                  <DocInfo>
                    <DocTopRow>
                      <DocNo>{docNo || "Untitled"}</DocNo>
                      <DocAmount>₹{formatINR(amount)}</DocAmount>
                    </DocTopRow>
                    <DocMetaRow>
                      <MetaItem>
                        <User size={12} />
                        {doc.customer_name || "Customer"}
                      </MetaItem>
                      {date && (
                        <MetaItem>
                          <Calendar size={12} />
                          {date}
                        </MetaItem>
                      )}
                    </DocMetaRow>
                  </DocInfo>
                  <ChevronWrap>
                    <ChevronRight size={16} />
                  </ChevronWrap>
                </DocCard>
              );
            })}
          </DocList>
        </ModalBody>

        <ModalActions>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
        </ModalActions>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

const CountPill = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.12);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
  letter-spacing: 0.02em;
`;

const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(360px, 45vh);
  overflow-y: auto;
  padding-right: 2px;
`;

const DocCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 14px 14px 12px;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fafafa;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background: #fff;
    border-color: rgba(201, 77, 170, 0.35);
    box-shadow: 0 8px 22px rgba(201, 77, 170, 0.1);
    transform: translateY(-1px);
  }
`;

const DocIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: linear-gradient(145deg, #fff9f0 0%, #fdf0fa 100%);
  border: 1px solid rgba(201, 77, 170, 0.16);
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DocTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const DocNo = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocAmount = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #16a34a;
  flex-shrink: 0;
`;

const DocMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);

  svg {
    color: rgba(201, 77, 170, 0.7);
    flex-shrink: 0;
  }
`;

const ChevronWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(23, 23, 23, 0.28);
  flex-shrink: 0;

  ${DocCard}:hover & {
    color: #c94daa;
  }
`;
